import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Search, ShoppingBag, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/lib/cart";
import { INSTAGRAM_URL, SUPPORT_EMAIL } from "@/lib/content";

const LINKS = [
  { to: "/shop", label: "SHOP", id: "nav-link-shop" },
  { to: "/about", label: "ABOUT", id: "nav-link-about" },
  { to: "/contact", label: "CONTACT", id: "nav-link-contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="header-logo" aria-label="Requiem Fever home" className="shrink-0">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-10 md:flex">
            <div className="group relative" data-testid="nav-shop-wrapper">
              <NavLink
                to="/shop"
                data-testid="nav-link-shop"
                className={({ isActive }) =>
                  `group relative flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.3em] transition-colors ${
                    isActive ? "text-gold" : "text-bone/70 group-hover:text-bone"
                  }`
                }
              >
                SHOP
                <ChevronDown className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180" strokeWidth={1.5} />
              </NavLink>
              <div className="invisible absolute left-0 top-full z-10 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                <div className="min-w-60 border border-line bg-surface py-2 shadow-2xl shadow-black/60">
                  <Link to="/collections/jojo-drop-001" data-testid="nav-collection-jojo" className="block px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/70 transition-colors hover:bg-violet/10 hover:text-gold">
                    01 — JOJO DROP 001
                  </Link>
                  <Link to="/collections/evangelion-drop" data-testid="nav-collection-evangelion" className="block px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/70 transition-colors hover:bg-violet/10 hover:text-gold">
                    02 — EVANGELION DROP 002
                  </Link>
                  <Link to="/shop" data-testid="nav-all-collections" className="block border-t border-line px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50 transition-colors hover:text-bone">
                    ALL COLLECTIONS
                  </Link>
                </div>
              </div>
            </div>
            <NavLink
              to="/about"
              data-testid="nav-link-about"
              className={({ isActive }) =>
                `group relative font-mono text-[11px] uppercase tracking-[0.3em] transition-colors ${
                  isActive ? "text-gold" : "text-bone/70 hover:text-bone"
                }`
              }
            >
              ABOUT
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </NavLink>
            <NavLink
              to="/contact"
              data-testid="nav-link-contact"
              className={({ isActive }) =>
                `group relative font-mono text-[11px] uppercase tracking-[0.3em] transition-colors ${
                  isActive ? "text-gold" : "text-bone/70 hover:text-bone"
                }`
              }
            >
              CONTACT
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </NavLink>
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              data-testid="header-search-button"
              aria-label="Browse the drop"
              onClick={() => navigate("/shop")}
              className="p-2.5 text-bone/70 transition-colors hover:text-gold"
            >
              <Search className="h-[18px] w-[18px]" strokeWidth={1.5} />
            </button>
            <button
              data-testid="header-bag-button"
              aria-label="Open shopping bag"
              onClick={() => setOpen(true)}
              className="relative p-2.5 text-bone/70 transition-colors hover:text-gold"
            >
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.5} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    key={count}
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.4, opacity: 0 }}
                    data-testid="bag-count"
                    className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center bg-gold px-1 font-mono text-[9px] font-semibold text-ink"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="p-2.5 text-bone/70 transition-colors hover:text-gold md:hidden"
            >
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex flex-col bg-ink/[0.98] backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between border-b border-white/10 px-5">
              <Logo />
              <button
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
                className="p-2.5 text-bone/70 hover:text-gold"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-6">
              {[{ to: "/", label: "HOME", id: "mobile-link-home" }, { to: "/collections/jojo-drop-001", label: "JOJO DROP 001", id: "mobile-link-jojo" }, { to: "/collections/evangelion-drop", label: "EVANGELION DROP 002", id: "mobile-link-eva" }, ...LINKS.map((l) => ({ ...l, id: `mobile-${l.id}` }))].map(
                (l, i) => (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0, x: -28 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={l.to}
                      data-testid={l.id}
                      className="block border-b border-white/10 py-4 font-display text-5xl tracking-wide text-bone transition-colors hover:text-gold"
                    >
                      {l.label}
                    </Link>
                  </motion.div>
                )
              )}
            </nav>
            <div className="px-6 pb-10 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">
              <p>{SUPPORT_EMAIL}</p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-menu-instagram-link"
                className="mt-2 inline-block text-gold"
              >
                @requiemfever
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
